'use client';

import { useEffect, useCallback, useState, useRef } from 'react';
import { onMessageListener } from 'utils/firebase';
import fcmService from 'services/fcm';
import { useNotificationStore } from 'stores/notificationStore';
import { useAuthStore } from 'stores/authStore';
import type { Notification } from 'stores/types';

interface NotificationPayload {
  messageId?: string;
  notification?: {
    title?: string;
    body?: string;
    icon?: string;
    badge?: string;
  };
  data?: {
    type?: string;
    action?: string;
    url?: string;
    [key: string]: any;
  };
}

export const useNotificationManager = () => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [isManuallyDisabled, setIsManuallyDisabled] = useState(false);

  const { addNotification, notificationsEnabled } = useNotificationStore();
  const { currentUser } = useAuthStore();

  const unsubscribeRef = useRef<(() => void) | null>(null);

  /**
   * Check current permission status
   */
  const checkPermissionStatus = useCallback(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPermission(Notification.permission);
      return Notification.permission;
    }
    return 'default';
  }, []);

  /**
   * Handle incoming notification
   */
  const handleIncomingNotification = useCallback((payload: NotificationPayload) => {
    console.log('Handling incoming notification:', payload);

    // Create notification object for the store
    const notification: Notification = {
      id: payload.messageId || Date.now().toString(),
      title: payload.notification?.title || 'Thông báo mới',
      body: payload.notification?.body,
      content: payload.notification?.body,
      type: payload.data?.type || 'info',
      createdAt: new Date().toISOString(),
      isRead: false,
      data: payload.data || {}
    };

    // Add to notification store
    addNotification(notification);

    // Show browser notification if app is not focused
    if (document.hidden && fcmService.checkPermission()) {
      const notificationOptions: NotificationOptions = {
        body: notification.body || notification.content || '',
        icon: payload.notification?.icon || '/icons/icon-192x192.svg',
        badge: payload.notification?.badge || '/icons/icon-72x72.svg',
        tag: notification.id,
        data: payload.data,
        requireInteraction: false
      };

      // Create browser notification
      new Notification(notification.title, notificationOptions)
        .onclick = () => {
          window.focus();

          // Handle navigation if URL is provided
          if (payload.data?.url) {
            window.location.href = payload.data.url;
          }
        };
    }

    // Handle automatic navigation
    if (payload.data?.action === 'navigate' && payload.data?.url) {
      // Only auto-navigate if the app is focused
      if (!document.hidden) {
        window.location.href = payload.data.url;
      }
    }
  }, [addNotification]);

  /**
   * Initialize notification system
   */
  const initialize = useCallback(async () => {
    if (isInitialized || !currentUser) {
      return;
    }

    setIsLoading(true);

    try {
      // Check current permission
      const currentPermission = checkPermissionStatus();

      if (currentPermission === 'granted') {
        // Initialize FCM service
        const initialized = await fcmService.initialize();

        if (initialized) {
          // Get the FCM token
          const token = await fcmService.getToken();
          setFcmToken(token);

          // Setup foreground message listener
          unsubscribeRef.current = onMessageListener(handleIncomingNotification);

          setIsInitialized(true);
          console.log('Notification manager initialized successfully');
        }
      }
    } catch (error) {
      console.error('Failed to initialize notification manager:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, isInitialized, checkPermissionStatus, handleIncomingNotification]);

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);

    try {
      // Clear the manually disabled flag when requesting permission
      setIsManuallyDisabled(false);

      // Request permission from browser
      const granted = await fcmService.requestPermission();
      setPermission(granted ? 'granted' : 'denied');

      if (granted) {
        // Initialize FCM after permission is granted
        const initialized = await fcmService.initialize();

        if (initialized) {
          // Get the FCM token
          const token = await fcmService.getToken();
          setFcmToken(token);

          // Setup foreground message listener if not already setup
          if (!unsubscribeRef.current) {
            unsubscribeRef.current = onMessageListener(handleIncomingNotification);
          }

          setIsInitialized(true);
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Failed to request permission:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [handleIncomingNotification]);

  /**
   * Disable notifications
   */
  const disableNotifications = useCallback(async () => {
    setIsLoading(true);

    try {
      // Mark as manually disabled
      setIsManuallyDisabled(true);

      // Update device status to inactive
      await fcmService.updateDeviceStatus('INACTIVE');

      // Cleanup FCM service
      await fcmService.cleanup();

      // Clear local state
      setIsInitialized(false);
      setFcmToken(null);

      // Unsubscribe from messages
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      console.log('Notifications disabled');
    } catch (error) {
      console.error('Failed to disable notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Enable notifications
   */
  const enableNotifications = useCallback(async () => {
    if (permission === 'granted') {
      setIsLoading(true);

      try {
        // Clear the manually disabled flag
        setIsManuallyDisabled(false);

        // Re-initialize FCM service
        const initialized = await fcmService.initialize();

        if (initialized) {
          // Update device status to active
          await fcmService.updateDeviceStatus('ACTIVE');

          // Get the FCM token
          const token = await fcmService.getToken();
          setFcmToken(token);

          // Setup foreground message listener if not already setup
          if (!unsubscribeRef.current) {
            unsubscribeRef.current = onMessageListener(handleIncomingNotification);
          }

          setIsInitialized(true);
          console.log('Notifications enabled');
          return true;
        }
      } catch (error) {
        console.error('Failed to enable notifications:', error);
      } finally {
        setIsLoading(false);
      }
    } else if (permission === 'default') {
      // Need to request permission first
      return requestPermission();
    }

    return false;
  }, [permission, handleIncomingNotification, requestPermission]);

  /**
   * Toggle notifications on/off
   */
  const toggleNotifications = useCallback(async (enabled: boolean) => {
    if (enabled) {
      return enableNotifications();
    } else {
      await disableNotifications();
      return true;
    }
  }, [enableNotifications, disableNotifications]);

  /**
   * Initialize on mount when user is logged in and notifications are enabled
   */
  useEffect(() => {
    // Only auto-initialize if:
    // 1. User is logged in
    // 2. Notifications haven't been manually disabled
    // 3. Notifications are enabled in the store
    if (currentUser && !isManuallyDisabled && notificationsEnabled !== false) {
      initialize();
    }
  }, [currentUser, isManuallyDisabled, notificationsEnabled, initialize]);

  /**
   * Cleanup on unmount or logout
   */
  useEffect(() => {
    if (!currentUser && isInitialized) {
      // User logged out - delete FCM token
      fcmService.deleteToken();

      // Cleanup
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      setIsInitialized(false);
      setFcmToken(null);
    }
  }, [currentUser, isInitialized]);

  /**
   * Cleanup on component unmount
   */
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return {
    permission,
    isInitialized,
    isLoading,
    fcmToken,
    requestPermission,
    disableNotifications,
    enableNotifications,
    toggleNotifications,
    checkPermission: fcmService.checkPermission.bind(fcmService)
  };
};